import type { ClinicalRecordPrintPatient } from './clinical-record-print';
import {
  DOC_BASE_STYLES,
  DOC_FOOTER_STYLES,
  buildDocFooterHtml,
  buildDocHeaderHtml,
  dash,
  esc,
  openPrintWindow,
  resolveLogoDataUri,
  row,
  type Translate,
} from './print-shared';

export interface LabOrderRequest {
  lab_name?: string | null;
  panel_name?: string | null;
  status: string;
  notes?: string | null;
}

export interface LabOrderPrintInput {
  patient: ClinicalRecordPrintPatient;
  chief_complaint?: string | null;
  primary_diagnosis?: string | null;
  requests: LabOrderRequest[];
}

function buildRequestHtml(r: LabOrderRequest, index: number, t: Translate): string {
  const status_label = esc(t(`clinical.lab_status_${r.status}`));
  return `
    <li class="lab-item">
      <p class="lab-headline">
        <span class="lab-n">${index + 1}.-</span>
        <span class="lbl">Laboratorio:</span>${dash(r.lab_name)}
      </p>
      <p class="row"><span class="lbl">Estudio solicitado:</span>${dash(r.panel_name)}</p>
      <p class="row"><span class="lbl">Estado:</span>${status_label}</p>
      ${r.notes ? `<p class="row"><span class="lbl">Notas:</span>${esc(r.notes)}</p>` : ''}
    </li>`;
}

function buildRequestsHtml(requests: LabOrderRequest[], t: Translate): string {
  if (requests.length === 0) return `<p class="empty">Sin estudios de laboratorio registrados.</p>`;
  return `<ol class="lab-list">${requests.map((r, i) => buildRequestHtml(r, i, t)).join('')}</ol>`;
}

function buildMotivoHtml(chief_complaint: string | null | undefined, primary_diagnosis: string | null | undefined): string {
  if (!chief_complaint && !primary_diagnosis) return `<p class="empty">Sin motivo registrado.</p>`;
  return [
    chief_complaint ? row('Motivo de consulta', chief_complaint) : '',
    primary_diagnosis ? row('Diagnóstico presuntivo', primary_diagnosis) : '',
  ]
    .filter(Boolean)
    .join('');
}

function buildDocumentHtml(input: LabOrderPrintInput, t: Translate, logo_data_uri: string | null): string {
  const { patient } = input;
  return `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8" />
<title>Orden de laboratorio — ${esc(patient.patient_name)}</title>
<style>
  ${DOC_BASE_STYLES}
  ${DOC_FOOTER_STYLES}
  .lab-list { list-style: none; padding: 0; margin: 0; }
  .lab-item { margin-bottom: 12px; border-bottom: 1px dashed #cfe3e8; padding-bottom: 8px; }
  .lab-item:last-child { border-bottom: none; }
  .lab-headline { font-size: 13px; margin: 0 0 2px; }
  .lab-n { color: #666; margin-right: 4px; }
</style>
</head>
<body>
  <div class="page">
    <div class="sheet">
      ${buildDocHeaderHtml({
        logo_data_uri,
        doc_title: 'Orden de Laboratorio',
        corner_label: 'No. Expediente',
        corner_value: patient.pet_record_code,
      })}

      <div class="tutor-row">
        <span><span class="lbl">Tutor/Propietario:</span>${dash(patient.owner_name)}</span>
        <span><span class="lbl">Fecha:</span>${esc(patient.visit_date)}</span>
      </div>

      <p class="section-title">Reseña</p>
      <div class="resena-grid">
        ${row('Paciente', patient.patient_name)}
        ${row('Sexo', patient.sex_label)}
        ${row('Raza', patient.breed)}
        ${row('Especie', patient.species)}
        ${row('Edad', patient.age_label)}
        ${row('Peso', patient.weight_label)}
        ${row('Teléfono', patient.owner_phone)}
        ${row('Expediente', patient.pet_record_code)}
      </div>

      <div class="box">
        <p class="box-title">Motivo de la solicitud</p>
        ${buildMotivoHtml(input.chief_complaint, input.primary_diagnosis)}
      </div>

      <div class="box tall">
        <p class="box-title">Estudios de Laboratorio Solicitados</p>
        ${buildRequestsHtml(input.requests, t)}
      </div>

      <div class="signature-block">
        <p class="row">Médico solicitante: ${esc(patient.vet_name)}</p>
        <p class="row">Céd. Prof.: ${dash(patient.vet_license)}</p>
      </div>

      ${buildDocFooterHtml()}
    </div>
  </div>
</body>
</html>`;
}

/**
 * Opens a print-ready "Orden de Laboratorio" document — every lab request on the visit,
 * showing which lab and which panel/study was requested (not the individual result lines —
 * this is the order slip, not a results report). Reuses the same header/footer chrome as
 * the prescription and clinical-record PDFs. Returns false if the popup was blocked.
 */
export async function printLabOrder(input: LabOrderPrintInput, t: Translate): Promise<boolean> {
  const logo_data_uri = await resolveLogoDataUri();
  const html = buildDocumentHtml(input, t, logo_data_uri);
  return openPrintWindow(html);
}
